// EditBusiness.tsx

import React, { useEffect, useState } from "react";
import BusinessForm from "./BusinessForm";
import { ProfileData } from "../types/profile";
import { useBusinessForm } from "../hooks/useBusinessForm";
import api from "../../../../features/auth/axiosConfig";
import { notifyError, notifySuccess } from "../../../../assets/toastService";
import { ModalWrapper } from "../../../components/ModalWrapper";
import CommonButtons from "../../../../shared/components/CommonButtons";

interface EditBusinessProps {
  profileData: ProfileData | null;
  onClose: () => void;
  onSave: () => void;
}

const EditBusiness: React.FC<EditBusinessProps> = ({ profileData, onClose, onSave }) => {
  const { formData, handleChange, setFormData } = useBusinessForm(profileData);

  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    if (!profileData) return;

    const normalizeProfileData = {
      ...profileData,
      dob: profileData.dob ? new Date(typeof profileData.dob === "string" ? profileData.dob : profileData.dob?.$date).toISOString().split("T")[0] : "",
    };

    const currentData = JSON.stringify(formData);
    const originalData = JSON.stringify(normalizeProfileData);

    setIsChanged(currentData !== originalData);
  }, [formData, profileData]);

  useEffect(() => {
    if (!profileData) return;

    const normalizedFormData = {
      ...formData,
      timeZone: formData.timeZone ? Number(formData.timeZone) : null,
    };

    const normalizedProfileData = {
      ...profileData,
      dob: profileData.dob ? new Date(typeof profileData.dob === "string" ? profileData.dob : profileData.dob?.$date).toISOString().split("T")[0] : "",
      timeZone: profileData.timeZone ? Number(profileData.timeZone) : null,
    };

    setIsChanged(JSON.stringify(normalizedFormData) !== JSON.stringify(normalizedProfileData));
  }, [formData, profileData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profileData) return;

    const emailValue = formData.emails?.[0]?.email?.trim() || "";
    const phoneValue = formData.phones?.[0]?.phone?.trim() || "";
    // Phone validation
    if (phoneValue && phoneValue.length !== 8) {
      setPhoneError("Phone number must be exactly 8 digits.");
      return;
    }

    setPhoneError("");
    // Email validation
    if (emailValue) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(co|com)$/i;

      if (!emailRegex.test(emailValue)) {
        notifyError("Please enter a valid email address.");
        return;
      }
    }

    // Build changed fields only
    const updates: any = {};

    Object.keys(formData).forEach((key) => {
      const formValue = (formData as any)[key];
      const originalValue = (profileData as any)[key];

      const isFieldChanged = JSON.stringify(formValue) !== JSON.stringify(originalValue);

      if (isFieldChanged) {
        updates[key] = formValue;
      }
    });

    // Convert timezone
    if ("timeZone" in updates) {
      updates.timeZone = updates.timeZone ? Number(updates.timeZone) : null;
    }

    if (Object.keys(updates).length === 0) {
      notifyError("No changes detected.");
      return;
    }

    setLoading(true);

    try {
      await api.put(`/pos/v2/profile/${profileData.orgId}`, updates, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      notifySuccess("Business profile updated successfully!");

      setTimeout(() => {
        onSave();
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Error updating business profile:", error);

      notifyError("Failed to update business profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!profileData) return null;

  return (
    <ModalWrapper title="Edit Business Profile" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <BusinessForm formData={formData} handleChange={handleChange} setFormData={setFormData} phoneError={phoneError} />

        <div className="flex justify-end gap-3 pt-2">
          <CommonButtons className="w-36" label="CANCEL" variant="outlineDanger" onClick={onClose} type="button" />

          <CommonButtons className="w-36" label={loading ? "Saving..." : "Confirm"} disabled={loading || !isChanged} type="submit" />
        </div>
      </form>
    </ModalWrapper>
  );
};

export default EditBusiness;
